import React, { useCallback, useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";
import { Loader2, Mic, MicOff, Phone, PhoneOff } from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { cn } from "../../lib/utils";

import api from "../../services/api";

// Ligação de voz pela Wavoip, que trafega a chamada sobre a sessão do
// WhatsApp da conexão.
//
// Antes isto era um iframe para app.wavoip.com com o token na URL. Tinha três
// problemas: o cronômetro começava no clique (e não quando a pessoa atendia),
// não havia como mudar o microfone nem saber se a chamada foi recusada, e o
// endereço/parâmetro não batiam com os da Wavoip. Agora falamos com a
// biblioteca oficial, que abre um socket e avisa cada mudança de estado.
//
// O áudio é tratado dentro da biblioteca: ela sobe o transporte ao atender
// (`audio_transport:create`) e o derruba ao encerrar. É esse par que diz, de
// verdade, quando a conversa começou — por isso o cronômetro nasce dali.

const ESTADOS = {
  conectando: "Conectando…",
  chamando: "Chamando…",
  falando: "Em conversa",
  encerrada: "Chamada encerrada"
};

const formatarDuracao = (segundos) => {
  const m = String(Math.floor(segundos / 60)).padStart(2, "0");
  const s = String(segundos % 60).padStart(2, "0");

  return `${m}:${s}`;
};

const CallButton = ({ ticket }) => {
  const [token, setToken] = useState(null);
  const [verificando, setVerificando] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [estado, setEstado] = useState("conectando");
  const [mudo, setMudo] = useState(false);
  const [duracao, setDuracao] = useState(0);

  const instancia = useRef(null);
  const cronometro = useRef(null);

  const numero = String(ticket?.contact?.number || "").replace(/\D/g, "");

  // Contato só-LID não tem telefone; ligar para um LID não existe.
  const podeLigar = Boolean(token) && numero.length >= 10 && !ticket?.isGroup;

  const verificar = useCallback(async () => {
    if (!ticket?.whatsappId) {
      setVerificando(false);
      return;
    }

    try {
      const { data } = await api.get(`/wavoip/${ticket.whatsappId}`);
      setToken(data?.token || null);
    } catch (err) {
      // Conexão sem Wavoip é o caso comum: o botão simplesmente não aparece.
      setToken(null);
    }

    setVerificando(false);
  }, [ticket?.whatsappId]);

  useEffect(() => {
    verificar();
  }, [verificar]);

  // Solta o socket e o microfone ao sair da tela — sem isto a chamada
  // continuaria viva depois de fechar o atendimento.
  const desligar = useCallback(() => {
    clearInterval(cronometro.current);

    const atual = instancia.current;

    instancia.current = null;

    if (atual) {
      try {
        atual.endCall();
      } catch (err) {
        // Já pode ter caído do outro lado; encerrar de novo não é erro.
      }

      try {
        atual.socket?.disconnect();
      } catch (err) {
        // idem
      }
    }
  }, []);

  useEffect(() => desligar, [desligar]);

  const encerrar = useCallback(() => {
    desligar();
    setAberto(false);
    setEstado("conectando");
    setDuracao(0);
    setMudo(false);
  }, [desligar]);

  const ligar = async () => {
    if (!podeLigar) {
      toast.info("Este contato não tem telefone para receber ligação.");
      return;
    }

    setAberto(true);
    setEstado("conectando");
    setDuracao(0);
    setMudo(false);

    try {
      // Import tardio de propósito. A wavoip-api chama
      // `navigator.mediaDevices.addEventListener` no corpo do módulo, e
      // `mediaDevices` só existe em contexto seguro: importada no topo, ela
      // estourava na carga do bundle e o painel inteiro ficava em branco em
      // qualquer instalação servida por HTTP (IP sem domínio, rede interna).
      // Carregada aqui, o erro fica restrito a quem realmente vai ligar.
      const { default: Wavoip } = await import("wavoip-api");
      const wav = new Wavoip();
      const inst = wav.connect(token);

      instancia.current = inst;

      inst.socket.on("connect", () => {
        setEstado("chamando");
        inst.callStart({ whatsappid: numero }).catch(() => {
          toast.error("A Wavoip recusou a chamada. Confira o token da conexão.");
          encerrar();
        });
      });

      // Só existe transporte de áudio depois que a pessoa atende: é o marco
      // certo para começar a contar a duração.
      inst.socket.on("audio_transport:create", () => {
        setEstado("falando");
        clearInterval(cronometro.current);
        cronometro.current = setInterval(() => setDuracao((d) => d + 1), 1000);
      });

      inst.socket.on("audio_transport:terminate", () => {
        clearInterval(cronometro.current);
        setEstado("encerrada");
      });

      inst.socket.on("disconnect", () => {
        clearInterval(cronometro.current);
        setEstado("encerrada");
      });

      inst.socket.on("error", () => {
        toast.error("Falha na ligação.");
        encerrar();
      });
    } catch (err) {
      toast.error("Não foi possível iniciar a ligação.");
      encerrar();
    }
  };

  const alternarMudo = async () => {
    const inst = instancia.current;

    if (!inst) return;

    try {
      if (mudo) {
        await inst.unmute();
      } else {
        await inst.mute();
      }

      setMudo((m) => !m);
    } catch (err) {
      toast.error("Não foi possível mudar o microfone.");
    }
  };

  if (verificando || !token) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        aria-label="Ligar para o contato"
        title="Ligar"
        onClick={ligar}
      >
        <Phone className="h-4 w-4" />
      </Button>

      <Dialog
        open={aberto}
        onOpenChange={(v) => {
          if (!v) encerrar();
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-4 py-2">
            <span
              className={cn(
                "flex h-16 w-16 items-center justify-center rounded-full",
                estado === "falando"
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                  : "bg-muted text-muted-foreground"
              )}
            >
              {estado === "conectando" ? (
                <Loader2 className="h-7 w-7 animate-spin" />
              ) : (
                <Phone
                  className={cn(
                    "h-7 w-7",
                    estado === "chamando" && "animate-pulse"
                  )}
                />
              )}
            </span>

            <div className="text-center">
              <p className="text-base font-semibold">{ticket?.contact?.name}</p>
              <p className="text-xs tabular-nums text-muted-foreground">
                {numero}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {ESTADOS[estado]}
              </p>
              {estado === "falando" && (
                <p className="text-sm tabular-nums text-muted-foreground">
                  {formatarDuracao(duracao)}
                </p>
              )}
            </div>

            <div className="flex w-full gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={alternarMudo}
                disabled={estado !== "falando"}
              >
                {mudo ? (
                  <MicOff className="h-4 w-4" />
                ) : (
                  <Mic className="h-4 w-4" />
                )}
                {mudo ? "Reativar" : "Mudo"}
              </Button>

              <Button
                variant="destructive"
                className="flex-1"
                onClick={encerrar}
              >
                <PhoneOff className="h-4 w-4" />
                Encerrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CallButton;
